import React from 'react';
import {
  RightSettingsPanel,
  SettingsSection,
  SettingsSectionTitle,
  SettingLabel,
  SettingRange,
  SettingRow,
  CheckboxLabel,
} from './styles';
import DropDown from '../../../Input/dropdown/Dropdown';
import { Checkbox } from 'components/Buttons';
import useChatbot from 'hooks/useChatbot';
import utils from 'utils';

interface SettingsPanelProps {
  loading: boolean;
}

export const SettingsPanel: React.FC<SettingsPanelProps> = ({ loading }) => {
  const {
    selectedModel,
    models,
    setSelectedModel,
    temperature,
    setTemperature,
    useNeighbors,
    setUseNeighbors,
    suppressHistory,
    setSuppressHistory,
    suppressContext,
    setSuppressContext,
    toolsEnabled,
    setToolsEnabled,
    onlyValidated,
    setOnlyValidated,
  } = useChatbot();
  const t = utils.intl.useTranslator();

  return (
    <RightSettingsPanel>
      <SettingsSection>
        <SettingsSectionTitle>{utils.app.textTranslated('modelSettings', t, 'Model Settings')}</SettingsSectionTitle>
        <div>
          <SettingLabel>{utils.app.textTranslated('selectModel', t, 'Select Model')}</SettingLabel>

          <DropDown
            id="model-dropdown"
            defaultDropdownValue={{ label: selectedModel, value: selectedModel }}
            value={{ label: selectedModel, value: selectedModel }}
            options={models?.map((model) => ({ value: model, label: model }))}
            disabled={loading}
            onChange={(option) => setSelectedModel && setSelectedModel(option.value)}
          />
        </div>
      </SettingsSection>

      <SettingsSection>
        <SettingsSectionTitle>
          {utils.app.textTranslated('responseSettings', t, 'Response Settings')}
        </SettingsSectionTitle>
        <div>
          <SettingLabel htmlFor="temperature">
            {utils.app.textTranslated('temperature', t, 'Temperature')}: {temperature.toFixed(1)}
          </SettingLabel>
          <SettingRange
            id="temperature"
            min="0"
            max="1"
            step="0.1"
            value={temperature}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTemperature(parseFloat(e.target.value))}
            disabled={loading}
            style={
              {
                '--percent': `${temperature * 100}%`,
              } as React.CSSProperties
            }
          />
        </div>
      </SettingsSection>

      <SettingsSection>
        <SettingsSectionTitle>
          {utils.app.textTranslated('featureSettings', t, 'Feature Settings')}
        </SettingsSectionTitle>

        <SettingRow>
          <Checkbox
            name="use-neighbors"
            isActive={useNeighbors}
            action={(e) => setUseNeighbors && setUseNeighbors(e)}
          />
          <CheckboxLabel htmlFor="use-neighbors">
            {utils.app.textTranslated('useNeighbors', t, 'Use Neighbors')}
          </CheckboxLabel>
        </SettingRow>
        <SettingRow>
          <Checkbox
            name="suppress-history"
            isActive={suppressHistory}
            action={(e) => setSuppressHistory && setSuppressHistory(e)}
          />
          <CheckboxLabel htmlFor="suppress-history">
            {utils.app.textTranslated('suppressHistory', t, 'Suppress History')}
          </CheckboxLabel>
        </SettingRow>
        <SettingRow>
          <Checkbox
            name="suppress-context"
            isActive={suppressContext}
            action={(e) => setSuppressContext && setSuppressContext(e)}
          />
          <CheckboxLabel htmlFor="suppress-context">
            {utils.app.textTranslated('suppressContext', t, 'Suppress Context')}
          </CheckboxLabel>
        </SettingRow>
        <SettingRow>
          <Checkbox
            name="tools-enabled"
            isActive={toolsEnabled}
            action={(e) => setToolsEnabled && setToolsEnabled(e)}
          />
          <CheckboxLabel htmlFor="tools-enabled">
            {utils.app.textTranslated('toolsEnabled', t, 'Tools Enabled')}
          </CheckboxLabel>
        </SettingRow>
        <SettingRow>
          <Checkbox
            name="only-validated"
            isActive={onlyValidated}
            action={(e) => setOnlyValidated && setOnlyValidated(e)}
          />
          <CheckboxLabel htmlFor="only-validated">
            {utils.app.textTranslated('onlyValidated', t, 'Only Validated')}
          </CheckboxLabel>
        </SettingRow>
      </SettingsSection>
    </RightSettingsPanel>
  );
};
